/**
 * What you can play.
 *
 * Free games sit alongside bought ones with no distinction drawn, because from
 * where the player stands there isn't one — a library is "what I can start",
 * not "what I paid for". What you *cannot* play belongs in the store.
 *
 * ## The header holds the title, unlike the join screen's
 *
 * `.page-bar` is the same three-column shape both wear, but this page is
 * reached *through* the front door rather than from a link somebody sent, so
 * it does not need to introduce the brand — the middle column carries the
 * heading instead of the lockup. That is the whole difference, and it is why
 * the bar takes whatever it is given rather than drawing either one itself.
 */

import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCatalog } from '../use-catalog.js';
import { useFavourites } from '../use-favourites.js';
import { Icon } from '../render/Icon.js';
import { JoinWithCode } from './JoinWithCode.js';
import { matchesQuery, shelve } from '../render/shelves.js';
import { GameCard } from './GameCard.js';

/**
 * The heading over a shelf of games.
 *
 * Not `.section-title`, which is the small tracked label Account and the game
 * page use over a single card - it is a caption, and borrowing it here made
 * four real divisions of a long page read as four pieces of fine print.
 *
 * The rule running off the end is what does the dividing: a heading with a
 * line through the space beside it reads as the top of a section rather than
 * as a line of text that happens to be above one. The count is there because
 * it is free - the shelf knows - and because "how many have I got" is the
 * question somebody scanning their own library is usually asking.
 */
function ShelfTitle({
  name,
  count,
  seeAll,
}: {
  name: string;
  count: number;
  /**
   * Where "See All" goes, or absent when there is nowhere to go.
   *
   * Absent on a shelf you are already looking at on its own, and on the
   * not-yours shelf, which is one list rather than a slice of a longer one.
   */
  seeAll?: string | undefined;
}) {
  return (
    <h2 className="shelf-title">
      <span className="shelf-name">{name}</span>
      <span className="shelf-count" aria-hidden="true">
        {count}
      </span>
      {seeAll === undefined ? null : (
        <Link className="shelf-all" to={seeAll}>
          See All
          <Icon name="chevron-right" />
        </Link>
      )}
    </h2>
  );
}

export function Library() {
  const { catalog, error, retry } = useCatalog();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  /* One read for the page; every card on it shares the answer. */
  const favourites = useFavourites();

  if (error) {
    return (
      <div className="page narrow felt-page library-page">
        <h1 className="page-title page-heading">Games</h1>
        <p className="banner error">{error}</p>
        <button type="button" className="big-button" onClick={retry}>
          Try again
        </button>
      </div>
    );
  }

  if (!catalog) return <div className="splash">Dealing…</div>;

  const owned = catalog.games.filter((game) => game.owned);

  /*
   * Both in the URL, so "See All" is an ordinary link and the back button
   * undoes a filter. Component state would have made See All a button that
   * looked like a link and left the back button skipping the whole page.
   */
  const filter = params.get('filter') ?? '';
  const query = params.get('q') ?? '';

  /*
   * The tabs come from everything you own, not from what is showing.
   *
   * Reading them off the filtered set would delete every tab but the one you
   * had chosen the moment you chose it, which is a filter you cannot get out
   * of.
   */
  const tabs = shelve(owned).filter((shelf) => shelf.title !== null);

  const keep = (game: (typeof catalog.games)[number]) => matchesQuery(game, query);
  const shelves = shelve(owned.filter(keep)).filter(
    (shelf) => filter === '' || shelf.id === filter,
  );
  const playable = owned.filter(keep);
  const rest = catalog.games.filter((game) => !game.owned && keep(game));

  /* One shelf on screen is a page about that shelf: the cards wrap into a
     grid so you can see all of them, which is what "See All" promised. */
  const showingOne = filter !== '' || shelves.length <= 1;

  const withFilter = (next: string) => {
    const search = new URLSearchParams(params);
    if (next === '') search.delete('filter');
    else search.set('filter', next);
    return `?${search.toString()}`;
  };

  return (
    <div className="page felt-page library-page">
      {/*
        The way back to the front page, on a phone.

        On a wide screen the rail's lockup is this link and is always there.
        The rail is hidden below 900px and the tab bar that replaces it holds
        destinations inside the app, so on a phone there was no way out to the
        front page at all. Hidden rather than unmounted above the breakpoint,
        so this is not a second link to the same place beside the lockup.
      */}
      <Link className="page-back library-back" to="/">
        <Icon name="chevron-right" />
        <span>Back</span>
      </Link>

      {/*
        The page's name, and the way in to somebody else's table.

        On one line, with joining at the right. Browsing and being invited
        happen at the same moment - you are looking through the shelves and
        somebody reads you a code - so the two belong at the same level rather
        than one under the other.

        Not a second implementation of the code box: the landing page's own
        component, so it behaves identically in both places and keeps the
        behaviours that exist because somebody hit something - the focus that
        follows the reveal, the select-on-focus, the length guard.
      */}
      <div className="library-head">
        <h1 className="page-title page-heading">Games</h1>
        <div className="library-join">
          <JoinWithCode />
        </div>
      </div>

      <p className="page-lede">
        Find your next game. Classic favorites and new challenges, all in one place.
      </p>

      {/*
        Search, then the shelves as tabs.

        Both narrow the same list and both live in the URL, so they compose:
        searching inside a filter is filtering what is already narrowed rather
        than replacing it.
      */}
      <div className="library-controls">
        <label className="library-search">
          <Icon name="search" />
          <input
            type="search"
            value={query}
            placeholder="Search games…"
            aria-label="Search games"
            onChange={(event) => {
              const search = new URLSearchParams(params);
              if (event.target.value === '') search.delete('q');
              else search.set('q', event.target.value);
              setParams(search, { replace: true });
            }}
          />
        </label>

        {tabs.length > 1 ? (
          <nav className="library-tabs" aria-label="Filter games">
            <Link className={filter === '' ? 'library-tab current' : 'library-tab'} to={withFilter('')}>
              All Games
            </Link>
            {tabs.map((shelf) => (
              <Link
                key={shelf.id}
                className={filter === shelf.id ? 'library-tab current' : 'library-tab'}
                to={withFilter(shelf.id)}
              >
                {shelf.title}
              </Link>
            ))}
          </nav>
        ) : null}
      </div>

      {/*
        Sectioned, because one grid of twelve answers no question anybody
        arrives with. See `shelve` for what the sections are and why they are
        read off the catalog rather than tagged by hand.

        A library with only one section comes back unnamed, so somebody who
        owns three shedding games still sees one grid under the lede rather
        than a heading telling them what they already know.
      */}
      {shelves.map((shelf) => (
        <section className="shelf" key={shelf.id}>
          {shelf.title === null ? null : (
            <ShelfTitle
              name={shelf.title}
              count={shelf.games.length}
              {...(showingOne ? {} : { seeAll: withFilter(shelf.id) })}
            />
          )}
          <div className={showingOne ? 'game-grid' : 'game-grid shelf-row'}>
            {shelf.games.map((game) => (
              <GameCard
                key={game.id}
                game={game}
                starred={favourites.has(game.id)}
                onStar={() => favourites.toggle(game.id)}
              />
            ))}
          </div>
        </section>
      ))}

      {shelves.length === 0 && query !== '' ? (
        <p className="field-help">Nothing matches “{query}”.</p>
      ) : null}

      {/*
        Surprise Me (E6), as a card rather than a loose button.

        It was an outlined button floating between two grids, which read as a
        third game that had lost its picture. The mockup draws it as a prompt —
        a question, a line of reassurance, and the action at the end — which is
        the same shape the landing page's game-night card already had, so it is
        that component rather than a second one.

        Only over games you can actually start: offering something you do not
        own would be a shop pretending to be a shuffle. Hidden below two,
        because "pick one of the one thing you have" is not a surprise.

        It goes to the game's page rather than opening a table: the point is to
        break a decision nobody wants to make, not to take it out of their
        hands. You can still read what it is and back out.
      */}
      {playable.length > 1 ? (
        <section className="promo-card">
          <span className="promo-card-badge">
            <Icon name="dice" />
          </span>
          <div className="promo-card-text">
            <h2 className="promo-card-title">Not sure what to play?</h2>
            <p className="promo-card-note">We&apos;ll pick a good one for your group.</p>
          </div>
          <button
            type="button"
            className="pill-button"
            onClick={() => {
              const pick = playable[Math.floor(Math.random() * playable.length)];
              if (pick) navigate(`/game/${pick.id}`);
            }}
          >
            Surprise me
          </button>
        </section>
      ) : null}

      {owned.length === 0 ? (
        <p className="field-help">Nothing here yet. The store has the free ones too.</p>
      ) : null}

      {rest.length > 0 ? (
        <section className="shelf">
          <ShelfTitle name="Not yours yet" count={rest.length} />
          <div className="game-grid">
            {rest.map((game) => (
              <GameCard
                key={game.id}
                game={game}
                starred={favourites.has(game.id)}
                onStar={() => favourites.toggle(game.id)}
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

